// /app Home dashboard (BRIEFS C3.2, design _v2/design/Main.dc.html).
//
// Greeting + streak sentence, four tiles (today), Continue card, "Last 7 days" bars,
// "Questions solved — last 30 days" (correct/wrong bars + accuracy line; a day opens
// /app/day/<date>, 2026-10-30), "Your chapters" stacked bars,
// "Since you started" line. Numbers come from
// GET /api/v2/dashboard. No warning flags are shown here (SPEC §5).
import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppAuth } from '../../context/AppAuthContext';
import { useActivityArea } from '../../components/app/activity';
import { getDashboard, getOpenTest } from '../../api/v2';
import { OpenTestBanner } from '../../components/app/testParts';
import { IconBolt, IconChevronRight, IconNotes, IconTarget } from '../../components/app/icons';
import { C, FONT, button } from '../../components/app/theme';
import { LoadError } from '../../components/app/LoadState';

const BAR_MAX_PX = 90;
const BAR = '#C7D2FE';
const CHART_GOOD = '#059669';
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const card = {
  boxSizing: 'border-box', padding: 20, background: C.surface,
  border: `1px solid ${C.border}`, borderRadius: 12,
  display: 'flex', flexDirection: 'column', gap: 12,
};
const h2 = { fontFamily: FONT.heading, margin: 0, fontSize: 17, fontWeight: 600 };

// ------------------------------------------------------------------ words

const num = (n) => Number(n || 0).toLocaleString('en-IN');
const plural = (n, one, many) => `${num(n)} ${n === 1 ? one : many}`;

export function studyTime(seconds) {
  const m = Math.floor((seconds || 0) / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} h ${rest} min` : `${h} h`;
}

function totalTime(seconds) {
  const h = Math.floor((seconds || 0) / 3600);
  if (h < 1) return `${Math.floor((seconds || 0) / 60)} minutes studied`;
  return `${num(h)} ${h === 1 ? 'hour' : 'hours'} studied`;
}

function percent(part, whole) {
  return whole ? `${Math.round((100 * part) / whole)}% correct` : 'None yet today';
}

export function streakSentence(streak) {
  const n = (streak && streak.current) || 0;
  if (!n) return 'Study today to start your streak.';
  const days = n === 1 ? '1 day' : `${n} days`;
  if (!streak.today_done) return `You have studied ${days} in a row. Study today to keep it going.`;
  return `You have studied ${days} in a row. Keep going.`;
}

function weekdayOf(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

// ------------------------------------------------------------------ pieces

function Tile({ label, value, sub, streak = false, testId }) {
  const fg = streak ? C.streak : C.muted;
  return (
    <div data-testid={testId} style={{
      flex: '1 1 200px', boxSizing: 'border-box', padding: '18px 20px',
      background: streak ? C.streakBg : C.surface,
      border: `1px solid ${streak ? '#EBCB9A' : C.border}`, borderRadius: 12,
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ fontSize: 13, color: fg }}>{label}</div>
      <div style={{ fontSize: 34, fontWeight: 600, color: streak ? C.streak : C.text }}>{value}</div>
      <div style={{ fontSize: 13, color: fg }}>{sub}</div>
    </div>
  );
}

function ContinueLink({ to, Icon, title, sub, primary = false, testId }) {
  return (
    <Link to={to} data-testid={testId} style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', minHeight: 44,
      borderRadius: 10, textDecoration: 'none',
      border: primary ? 0 : `1px solid ${C.border}`,
      background: primary ? C.primary : 'transparent',
      color: primary ? '#FFFFFF' : C.text,
    }}>
      <Icon size={22} stroke={primary ? 'currentColor' : C.primary} />
      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ fontSize: 15, fontWeight: primary ? 600 : 500 }}>{title}</div>
        <div style={{ fontSize: 13, color: primary ? '#FFFFFF' : C.muted }}>{sub}</div>
      </div>
      <IconChevronRight stroke={primary ? 'currentColor' : C.muted} />
    </Link>
  );
}

function notesLink(last) {
  if (!last) return { to: '/app/notes', sub: 'Choose a chapter' };
  const unit = last.section_code ? `?unit=${encodeURIComponent(last.section_code)}` : '';
  const where = [last.number, last.title].filter(Boolean).join(' ');
  return {
    to: `/app/notes/${last.chapter_id}${unit}`,
    sub: `Chapter ${last.ncert_no}${where ? ` · ${where}` : ''}`,
  };
}

function ContinueCard({ last }) {
  const notes = notesLink(last);
  return (
    <div style={{ ...card, flex: '1 1 420px' }}>
      <h2 style={h2}>Continue</h2>
      <ContinueLink to={notes.to} Icon={IconNotes} title="Read notes" sub={notes.sub} testId="v2-continue-notes" />
      <ContinueLink to="/app/practice" Icon={IconTarget} title="Start a Practice Test"
        sub="Wrong and new questions come first" primary testId="v2-continue-practice" />
      <ContinueLink to="/app/revision" Icon={IconBolt} title="Start Revision"
        sub="One-liners and questions, mixed" testId="v2-continue-revision" />
    </div>
  );
}

function LastSevenDays({ last30 }) {
  const days = (last30 || []).slice(-7);
  const max = Math.max(1, ...days.map((d) => d.active_minutes));
  return (
    <div style={{ ...card, flex: '1 1 360px' }}>
      <h2 style={h2}>Last 7 days</h2>
      <div style={{ fontSize: 13, color: C.muted }}>Minutes studied each day</div>
      <div data-testid="v2-last7" style={{ flexGrow: 1, display: 'flex', alignItems: 'flex-end', gap: 12, minHeight: 150 }}>
        {days.map((d, i) => {
          const today = i === days.length - 1;
          const h = d.active_minutes ? Math.max(4, Math.round((BAR_MAX_PX * d.active_minutes) / max)) : 0;
          const label = { fontSize: 12, color: today ? C.text : C.muted, fontWeight: today ? 600 : 400 };
          return (
            <div key={d.day} style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={label}>{d.active_minutes}</div>
              <div style={{ width: '100%', height: h, borderRadius: '6px 6px 0 0', background: today ? C.primary : BAR }} />
              <div style={label}>{today ? 'Today' : weekdayOf(d.day)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// "Questions solved — last 30 days" (owner's requests 2026-10-30). One column per IST day,
// oldest on the left, today on the right:
//   - a stacked bar: correct (green) below, wrong (red) above — same colours as "Your
//     chapters"; the number on top = questions answered that day;
//   - under it, sharing the same days, the accuracy line (correct ÷ answered, 0–100 %);
//   - clicking a day with questions opens /app/day/<date> (that day's questions).
// Numbers: data.last30[].questions / .correct (daily_activity.questions_done /
// questions_correct — the same counts as the "Questions solved today" tile).
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const Q30_MAX_PX = 120;
const ACC_PX = 84;            // height of the accuracy strip
const ACC_PAD = 16;           // room above 100 % for the point labels
const ACC_LINE = C.primary;

export function dayParts(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return { d, month: MONTHS[m - 1], weekday: WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] };
}

const accOf = (d) => (d.questions ? Math.round((100 * (d.correct || 0)) / d.questions) : null);
const accY = (a) => ACC_PAD + ((100 - a) / 100) * (ACC_PX - ACC_PAD - 4);

function QuestionsLast30({ last30 }) {
  const navigate = useNavigate();
  const days = last30 || [];
  const counts = days.map((d) => d.questions || 0);
  const max = Math.max(1, ...counts);
  const sum = counts.reduce((a, b) => a + b, 0);
  const sumCorrect = days.reduce((a, d) => a + (d.correct || 0), 0);
  const daysWithQ = counts.filter((n) => n > 0).length;
  let best = null;
  days.forEach((d) => { if ((d.questions || 0) > 0 && (!best || d.questions >= best.questions)) best = d; });
  const bestWhen = best && (() => { const p = dayParts(best.day); return `${p.d} ${p.month}`; })();
  // On a narrow window the chart scrolls sideways: open on the newest days (today at the right).
  const scroller = useRef(null);
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [days.length]);

  // Accuracy line: x = centre of each column (as % of the width), y in px.
  const n = days.length || 1;
  const pts = days.map((d, i) => ({ i, a: accOf(d) })).filter((p) => p.a !== null);
  const linePts = pts.map((p) => `${((p.i + 0.5) / n) * 100},${accY(p.a)}`).join(' ');
  const open = (d) => { if (d.questions) navigate(`/app/day/${d.day}`); };

  return (
    <div style={{ ...card, gap: 14 }}>
      <style>{'.v2-q30col{border-radius:6px}.v2-q30col.v2-click:hover{background:#EEF2FF;cursor:pointer}'
        + '.v2-q30col.v2-click:focus-visible{outline:2px solid #4F46E5;outline-offset:1px}'}</style>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 style={h2}>Questions solved — last 30 days</h2>
          <div data-testid="v2-q30-summary" style={{ fontSize: 13, color: C.muted }}>
            {sum
              ? `${plural(sum, 'question', 'questions')} in 30 days · ${Math.round((100 * sumCorrect) / sum)}% correct · solved questions on ${daysWithQ} of 30 days · best day: ${num(best.questions)} on ${bestWhen}`
              : 'No questions solved in the last 30 days yet.'}
          </div>
          {sum > 0 && <div style={{ fontSize: 12, color: C.muted }}>Click a day to see its questions.</div>}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, paddingTop: 4 }}>
          <Legend color={CHART_GOOD} label="Correct" />
          <Legend color={C.bad} label="Wrong" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.muted }}>
            <svg width="22" height="12" aria-hidden="true"><line x1="1" y1="6" x2="21" y2="6" stroke={ACC_LINE} strokeWidth="2" /><circle cx="11" cy="6" r="3" fill={ACC_LINE} /></svg>
            Accuracy %
          </div>
        </div>
      </div>

      <div ref={scroller} style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 640 }}>
          {/* bars + dates */}
          <div data-testid="v2-q30" style={{ display: 'flex', alignItems: 'stretch' }}>
            {days.map((d, i) => {
              const q = d.questions || 0;
              const ok = Math.min(d.correct || 0, q);
              const bad = q - ok;
              const today = i === days.length - 1;
              const p = dayParts(d.day);
              const showMonth = i === 0 || p.d === 1;
              const h = q ? Math.max(4, Math.round((Q30_MAX_PX * q) / max)) : 0;
              const hOk = q ? Math.round((h * ok) / q) : 0;
              const label = { fontSize: 11, color: today ? C.text : C.muted, fontWeight: today ? 600 : 400, lineHeight: 1.2 };
              const words = `${p.weekday} ${p.d} ${p.month}: ${q ? `${plural(q, 'question', 'questions')} · ${ok} correct · ${bad} wrong · ${accOf(d)}% correct` : 'no questions'}`;
              return (
                <div key={d.day} data-testid="v2-q30-day" data-day={d.day}
                  className={`v2-q30col${q ? ' v2-click' : ''}`}
                  role={q ? 'button' : undefined} tabIndex={q ? 0 : undefined}
                  title={q ? `${words} — click to see them` : words} aria-label={words}
                  onClick={() => open(d)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(d); } }}
                  style={{
                    flex: '1 1 0', minWidth: 0, padding: '2px 2px 0', boxSizing: 'border-box',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}>
                  <div style={{ height: Q30_MAX_PX + 16, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 3 }}>
                    <div style={label}>{q ? num(q) : ''}</div>
                    {q ? (
                      <div style={{ width: '100%', height: h, display: 'flex', flexDirection: 'column', borderRadius: '4px 4px 0 0', overflow: 'hidden' }}>
                        <div style={{ height: h - hOk, background: C.bad }} />
                        <div style={{ height: hOk, background: CHART_GOOD }} />
                      </div>
                    ) : <div style={{ width: '100%', height: 2, borderRadius: 1, background: C.border }} />}
                  </div>
                  <div style={label}>{today ? 'Today' : p.d}</div>
                  <div style={{ ...label, minHeight: 13 }}>{showMonth && !today ? p.month : ''}</div>
                </div>
              );
            })}
          </div>

          {/* accuracy strip, same columns */}
          <div style={{ marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 2 }}>Accuracy % each day (correct ÷ answered) · dashed lines at 100%, 50% and 0%</div>
            <div data-testid="v2-q30-acc" style={{ position: 'relative', height: ACC_PX }}>
              {[100, 50, 0].map((g) => (
                <div key={g} style={{ position: 'absolute', left: 0, right: 0, top: accY(g), borderTop: `1px dashed ${C.border}` }} />
              ))}
              <svg width="100%" height={ACC_PX} viewBox={`0 0 100 ${ACC_PX}`} preserveAspectRatio="none"
                style={{ position: 'absolute', inset: 0, overflow: 'visible' }} aria-hidden="true">
                {pts.length > 1 && (
                  <polyline points={linePts} fill="none" stroke={ACC_LINE} strokeWidth="2"
                    strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                )}
              </svg>
              {pts.map((pt) => (
                <div key={pt.i} data-testid="v2-q30-acc-point" style={{
                  position: 'absolute', left: `${((pt.i + 0.5) / n) * 100}%`, top: accY(pt.a),
                  transform: 'translate(-50%, -50%)', pointerEvents: 'none',
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: ACC_LINE, border: '2px solid #FFFFFF' }} />
                  <div style={{
                    position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
                    fontSize: 10, fontWeight: 600, color: C.primaryDark, whiteSpace: 'nowrap',
                  }}>{pt.a}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.muted }}>
      <div style={{ width: 12, height: 12, borderRadius: 3, background: color }} />{label}
    </div>
  );
}

function ChapterRow({ ch }) {
  const pc = (n) => (ch.total ? `${(100 * n) / ch.total}%` : '0%');
  const started = ch.correct + ch.incorrect > 0;
  const words = started
    ? `${num(ch.correct)} correct · ${num(ch.incorrect)} wrong · ${num(ch.unattempted)} left`
    : `Not started · ${num(ch.unattempted)} left`;
  return (
    <div data-testid="v2-chapter-row" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
      <div style={{ flex: '1 1 260px', fontSize: 15 }}>
        {ch.ncert_no}. {ch.title}
        <span style={{ marginLeft: 8, fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>Class {ch.class_level}</span>
      </div>
      <div role="img" aria-label={words} style={{
        flex: '2 1 300px', display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: C.border,
      }}>
        {started && <div style={{ width: pc(ch.correct), background: CHART_GOOD }} />}
        {started && <div style={{ width: pc(ch.incorrect), background: C.bad }} />}
      </div>
      <div style={{ flex: '0 0 190px', fontSize: 13, color: C.muted }}>{words}</div>
    </div>
  );
}

function Chapters({ chapters }) {
  return (
    <div style={{ ...card, gap: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
        <h2 style={{ ...h2, flexGrow: 1 }}>Your chapters</h2>
        <Legend color={CHART_GOOD} label="Correct" />
        <Legend color={C.bad} label="Wrong" />
        <Legend color={C.border} label="Not done yet" />
      </div>
      {chapters && chapters.length
        ? chapters.map((ch) => <ChapterRow key={ch.chapter_id} ch={ch} />)
        : <div style={{ fontSize: 14, color: C.muted }}>Your chapters will show here when they are ready.</div>}
    </div>
  );
}

// ------------------------------------------------------------------ page

export default function AppHome() {
  const { user } = useAppAuth();
  useActivityArea('home');
  const first = ((user && user.full_name) || '').trim().split(/\s+/)[0] || 'there';
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['v2', 'dashboard'],
    queryFn: getDashboard,
    refetchInterval: 60000,
  });
  const openQ = useQuery({ queryKey: ['v2', 'open-test'], queryFn: getOpenTest });
  const openTest = openQ.data && openQ.data.test;

  const head = (sentence) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <h1 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 28, fontWeight: 600 }}>Hello, {first}</h1>
      <div data-testid="v2-streak-sentence" style={{ fontSize: 15, color: C.muted }}>{sentence}</div>
    </div>
  );

  if (isLoading || isError || !data) {
    return (
      <div style={{ maxWidth: 1000, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {head(isError ? 'Your numbers could not load.' : 'Loading your numbers…')}
        {isError && <LoadError onRetry={refetch} />}
      </div>
    );
  }

  const { today, total, streak } = data;
  return (
    <div data-testid="v2-home" style={{ maxWidth: 1000, display: 'flex', flexDirection: 'column', gap: 20 }}>
      {head(streakSentence(streak))}

      <OpenTestBanner test={openTest} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <Tile testId="v2-tile-questions" label="Questions solved today" value={num(today.questions)}
          sub={percent(today.correct, today.questions)} />
        <Tile testId="v2-tile-oneliners" label="One-liners done today" value={num(today.one_liners)}
          sub={percent(today.ol_correct, today.one_liners)} />
        <Tile testId="v2-tile-time" label="Time studied today" value={studyTime(today.active_seconds)}
          sub="Only working time is counted" />
        <Tile testId="v2-tile-streak" streak label="Streak" value={plural(streak.current, 'day', 'days')}
          sub={`Your best: ${plural(streak.best, 'day', 'days')}`} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <ContinueCard last={data.continue && data.continue.last_notes} />
        <LastSevenDays last30={data.last30} />
      </div>

      <QuestionsLast30 last30={data.last30} />

      <Chapters chapters={data.chapters} />

      <div data-testid="v2-since" style={{ fontSize: 13, color: C.muted }}>
        Since you started: {plural(total.questions, 'question', 'questions')} ·{' '}
        {plural(total.one_liners, 'one-liner', 'one-liners')} · {totalTime(total.active_seconds)}
      </div>
    </div>
  );
}
